import { ZfDivider, ZfStack, ZfTypography } from '@zafire/react';
import { useFormikContext } from 'formik';
import { PortfolioTypeRule } from 'pco-product-service-internal-consumer';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureFlagName } from '../../../services/portfolios/portfolioService';
import { PortfolioForm } from '../AddPortfolioDialog';
import { FormSelectField, toLookupOptions } from '../common/FormSelectField';
import { useGetDropdown } from '../hooks/useGetDropdown';
import useGetEmployees from '../hooks/useGetEmployees';
import useGetFeatureFlags from '../hooks/useGetFeatureFlags';
import { isServicingRoleVisible, resolveServicingRoleRule } from '../portfolioTypeFieldRules';

type ServicingRoleFieldsProps = {
  mandatory: boolean;
  readonly: boolean;
};

const ServicingRoleFields = ({ mandatory, readonly }: ServicingRoleFieldsProps) => {
  const { t } = useTranslation();
  const { servicingRoleTypeData, isLoading } = useGetDropdown();
  const { data: employeeData, isLoading: employeesLoading } = useGetEmployees();

  const servicingRoleTypeOptions = useMemo(() => toLookupOptions(servicingRoleTypeData ?? []), [servicingRoleTypeData]);
  const servicingUserOptions = useMemo(() => toLookupOptions(employeeData ?? []), [employeeData]);

  return (
    <>
      <ZfStack direction="row" spacing="small" style={{ marginTop: 'var(--padding-top-sm)' }}>
        <ZfTypography variant="label-medium" color="emphasis" weight="bold">
          {t('pb.portfoliodetail.servicingrole.title')}
        </ZfTypography>
        <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-xs)' }} />
      </ZfStack>
      <ZfStack direction="row" spacing="small">
        <FormSelectField
          name="servicingRoleType"
          label={t('pb.portfoliodetail.servicingroletype.label')}
          mandatory={mandatory}
          readonly={readonly}
          options={servicingRoleTypeOptions}
          isLoading={isLoading}
        />
        <FormSelectField
          name="servicingUser"
          label={t('pb.portfoliodetail.servicinguser.label')}
          mandatory={mandatory}
          readonly={readonly}
          options={servicingUserOptions}
          isLoading={employeesLoading}
        />
        <div style={{ flex: 1 }} />
      </ZfStack>
    </>
  );
};

type ServicingRoleSectionProps = {
  isStandaloneCreate?: boolean;
  portfolioRules?: readonly PortfolioTypeRule[];
};

export const ServicingRoleSection = ({
  isStandaloneCreate = false,
  portfolioRules = [],
}: ServicingRoleSectionProps) => {
  const { values } = useFormikContext<PortfolioForm>();
  const { data: featureFlags } = useGetFeatureFlags();
  const rocEnabled = featureFlags?.find((item) => item.name === FeatureFlagName.ROC_ENABLED)?.enabled ?? false;
  const portfolioType = String(values.portfolioType);

  if (!isServicingRoleVisible({ rules: portfolioRules, portfolioType, isStandaloneCreate, rocEnabled })) {
    return null;
  }

  const rule = resolveServicingRoleRule(portfolioRules, portfolioType);
  return isStandaloneCreate ? (
    <ServicingRoleFields mandatory={rule.required === true} readonly={rule.state === 'disabled'} />
  ) : (
    <ServicingRoleFields mandatory readonly={false} />
  );
};
